import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaidIcon from '@mui/icons-material/Paid';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import WarningIcon from '@mui/icons-material/Warning';
import {
    alpha,
    Box,
    Card,
    FormControl,
    IconButton,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Theme } from '@mui/system';
import dinero from 'dinero.js';
import moment from 'moment';
import React, { useState } from 'react';
import {
    OnlyFansMetricUserSalesDetails,
    OnlyFansMetricUserSalesDetailsRiskLevel,
} from '../../../../hooks/useSextforceMetricsUsersSalesDetails';
import { getPrettyDateDiff } from '../../../../utils/common';
import RangeProgressBar, { normalizeDateToPercentage } from '../../../common/RangeProgress';
import {
    getRiskLevelColor,
    getRiskLevelLabel,
    moneyToNumber,
    subscribedAtString,
    userTotalChargebacks,
    userTotalSpent,
} from './usersListHelpers';

export type UserRowWithTimeline = OnlyFansMetricUserSalesDetails & { timelineValue: number };

const formatMoney = (amount: number) => dinero({ amount: Math.round(amount * 100), currency: 'USD' }).toFormat();

const laterDate = (date1: Date, date2: Date) => (date1 > date2 ? date1 : date2);

/**
 * Status icon on the left of each card: risk warning wins, then spender, then freeloader.
 */
const StatusIcon = ({ row, hasChargebacks, theme }: { row: UserRowWithTimeline; hasChargebacks: boolean; theme: Theme }) => {
    if (row.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE) {
        const color = getRiskLevelColor(row.riskLevel, theme);

        return (
            <Tooltip title={getRiskLevelLabel(row.riskLevel)}>
                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: alpha(color, 0.15),
                        color,
                    }}
                >
                    <WarningIcon fontSize="medium" />
                </Box>
            </Tooltip>
        );
    }

    if (moneyToNumber(row.total) > 0) {
        return (
            <Tooltip title={hasChargebacks ? 'Spender with chargebacks' : 'Spender'}>
                <PaidIcon sx={{ fontSize: 40, color: hasChargebacks ? 'warning.main' : 'success.main' }} />
            </Tooltip>
        );
    }

    return (
        <Tooltip title="Freeloader">
            <ThumbDownIcon sx={{ fontSize: 32, color: 'error.main', mx: 0.5 }} />
        </Tooltip>
    );
};

/**
 * Kebab menu with the row actions that the table exposes through links and tooltips.
 */
const RowMenu = ({ row }: { row: UserRowWithTimeline }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const close = () => setAnchorEl(null);
    const profileUrl = `https://onlyfans.com/${row.userName}`;

    const copy = (text: string) => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).catch(() => undefined);
        }

        close();
    };

    return (
        <>
            <IconButton aria-label={`Actions for ${row.userName}`} onClick={e => setAnchorEl(e.currentTarget)} size="small">
                <MoreVertIcon color="primary" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
                <MenuItem
                    onClick={() => {
                        window.open(profileUrl, '_blank', 'noopener,noreferrer');
                        close();
                    }}
                >
                    <ListItemIcon>
                        <OpenInNewIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Open OnlyFans profile</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => copy(String(row.userId))}>
                    <ListItemIcon>
                        <ContentCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy user ID</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => copy(row.userName)}>
                    <ListItemIcon>
                        <ContentCopyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Copy username</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
};

type CardProps = {
    row: UserRowWithTimeline;
    metricStartDate: Date | undefined;
    timezone: string;
    theme: Theme;
    showEarningsAsGross: boolean;
    showEarningsWithSubscriptions: boolean;
};

const UserCard = ({ row, metricStartDate, timezone, theme, showEarningsAsGross, showEarningsWithSubscriptions }: CardProps) => {
    const total = userTotalSpent(row, showEarningsAsGross, showEarningsWithSubscriptions);
    const chargebacks = userTotalChargebacks(row, showEarningsAsGross, showEarningsWithSubscriptions);
    const hasTimeline = Boolean(metricStartDate && row.subscribedAt && row.firstSaleDate && row.lastSaleDate);
    const riskColor = row.riskLevel > OnlyFansMetricUserSalesDetailsRiskLevel.NONE ? getRiskLevelColor(row.riskLevel, theme) : undefined;
    const profileUrl = `https://onlyfans.com/${row.userName}`;

    const timelineStart = hasTimeline ? laterDate(new Date(row.subscribedAt), new Date(metricStartDate as Date)) : undefined;
    const timelineMin = timelineStart ? normalizeDateToPercentage(timelineStart, new Date(), new Date(row.firstSaleDate as Date)) : 0;
    const timelineMax = timelineStart ? normalizeDateToPercentage(timelineStart, new Date(), new Date(row.lastSaleDate as Date)) : 0;

    return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ p: 1.5 }}>
                {/* Fixed slot so the text column starts at the same x on every card, whichever icon shows */}
                <Box sx={{ width: 44, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <StatusIcon row={row} hasChargebacks={chargebacks > 0} theme={theme} />
                </Box>

                {/* One text column, one fact per line: when they subscribed, username, ID, what they spent.
                    The ID gets its own line so digits line up down the list and runs of consecutive IDs stand out. */}
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.4, color: riskColor || 'text.primary' }}>
                        {subscribedAtString(row.subscribedAt, timezone)}
                    </Typography>

                    <Link
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body1"
                        sx={{
                            color: riskColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            minWidth: 0,
                            width: 'fit-content',
                            maxWidth: '100%',
                        }}
                    >
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.userName}
                        </Box>
                        <OpenInNewIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                    </Link>

                    <Link
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body1"
                        color="text.secondary"
                        sx={{ width: 'fit-content', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                    >
                        {row.userId}
                    </Link>

                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pt: 0.75 }}>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ flexShrink: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                {formatMoney(total)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {showEarningsAsGross ? 'gross' : 'net'}
                            </Typography>
                            {chargebacks > 0 && (
                                <Typography variant="caption" color="error.main" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatMoney(chargebacks)} charged back
                                </Typography>
                            )}
                        </Stack>
                        {hasTimeline && (
                            <Tooltip
                                placement="top"
                                title={
                                    <Stack spacing={1}>
                                        <div style={{ fontWeight: 'bold' }}>Spending {row.timelineValue.toFixed(2)}% of total time</div>
                                        <div>
                                            <strong>First Sale</strong>
                                            <br />
                                            After{' '}
                                            {getPrettyDateDiff(new Date(metricStartDate as Date), new Date(row.firstSaleDate as Date))}
                                            <br />
                                            {moment(row.firstSaleDate).format('L hh:mm A')}
                                        </div>
                                        <div>
                                            <strong>Last Sale</strong>
                                            <br />
                                            After {getPrettyDateDiff(new Date(metricStartDate as Date), new Date(row.lastSaleDate as Date))}
                                            <br />
                                            {moment(row.lastSaleDate).format('L hh:mm A')}
                                        </div>
                                    </Stack>
                                }
                            >
                                {/* Bar is as tall as the amount's cap height and fills the rest of the row */}
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0, cursor: 'help' }}>
                                    <RangeProgressBar
                                        minValue={timelineMin}
                                        maxValue={timelineMax}
                                        sx={{
                                            flex: 1,
                                            height: 14,
                                            borderRadius: 7,
                                            '& .MuiLinearProgress-bar': { borderRadius: 7, backgroundColor: 'primary.main' },
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ minWidth: 38, textAlign: 'right', flexShrink: 0 }}
                                    >
                                        {Math.round(row.timelineValue)}%
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ mt: -0.5, mr: -0.5 }}>
                    <RowMenu row={row} />
                </Box>
            </Stack>
        </Card>
    );
};

type Props = {
    rows: UserRowWithTimeline[];
    loading: boolean;
    searchActive: boolean;
    metricStartDate: Date | undefined;
    timezone: string;
    theme: Theme;
    showEarningsAsGross: boolean;
    showEarningsWithSubscriptions: boolean;
    limit: number;
    offset: number;
    pageSizeOptions: number[];
    onLimitChange: (limit: number) => void;
    onOffsetChange: (offset: number) => void;
};

/**
 * Card-per-user layout for narrow screens. Expects rows already searched and sorted, and paginates them itself.
 */
const SextforceMetricsUsersWithRiskLevelCards = ({
    rows,
    loading,
    searchActive,
    metricStartDate,
    timezone,
    theme,
    showEarningsAsGross,
    showEarningsWithSubscriptions,
    limit,
    offset,
    pageSizeOptions,
    onLimitChange,
    onOffsetChange,
}: Props) => {
    const total = rows.length;
    const pageCount = Math.max(1, Math.ceil(total / limit));
    const page = Math.min(Math.floor(offset / limit), pageCount - 1);
    const pageRows = rows.slice(page * limit, page * limit + limit);
    const rangeStart = total === 0 ? 0 : page * limit + 1;
    const rangeEnd = Math.min(total, page * limit + limit);

    return (
        <Stack spacing={1}>
            {loading && rows.length === 0 && (
                <Stack spacing={1}>
                    {[0, 1, 2].map(i => (
                        <Skeleton key={i} variant="rounded" height={124} sx={{ borderRadius: 2 }} />
                    ))}
                </Stack>
            )}

            {!loading && rows.length === 0 && (
                <Card variant="outlined" sx={{ borderRadius: 2, py: 5, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        {searchActive ? 'No users match your search' : 'No subscribers found'}
                    </Typography>
                </Card>
            )}

            {pageRows.map(row => (
                <UserCard
                    key={row._id}
                    row={row}
                    metricStartDate={metricStartDate}
                    timezone={timezone}
                    theme={theme}
                    showEarningsAsGross={showEarningsAsGross}
                    showEarningsWithSubscriptions={showEarningsWithSubscriptions}
                />
            ))}

            <Card variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1}
                    sx={{ p: 1.5 }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary" component="label" htmlFor="users-rows-per-page">
                            Rows per page
                        </Typography>
                        <FormControl size="small">
                            <Select
                                id="users-rows-per-page"
                                native
                                value={limit}
                                onChange={e => {
                                    onLimitChange(parseInt(String(e.target.value), 10));
                                    onOffsetChange(0);
                                }}
                                inputProps={{ 'aria-label': 'Rows per page' }}
                            >
                                {pageSizeOptions.map(option => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {rangeStart}–{rangeEnd} of {total.toLocaleString()}
                    </Typography>
                    <Pagination
                        count={pageCount}
                        page={page + 1}
                        onChange={(_e: React.ChangeEvent<unknown>, newPage: number) => onOffsetChange((newPage - 1) * limit)}
                        siblingCount={0}
                        boundaryCount={1}
                        shape="rounded"
                        color="primary"
                        size="medium"
                    />
                </Stack>
            </Card>
        </Stack>
    );
};

export default SextforceMetricsUsersWithRiskLevelCards;
